import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiAdmin, resolveUploadUrl } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import InvitationUploader from "@/components/InvitationUploader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Save, Plus, Trash2, RefreshCw, Image as ImageIcon, Link as LinkIcon, Bolt } from "lucide-react";

const SOCIAL_FIELDS = [
  { key: "social_instagram", label: "Instagram Kullanıcı Adı", icon: "fa-brands fa-instagram", placeholder: "sahder", color: "#E4405F" },
  { key: "social_facebook", label: "Facebook Kullanıcı/Sayfa", icon: "fa-brands fa-facebook", placeholder: "sahder.dernek", color: "#1877F2" },
  { key: "social_youtube", label: "YouTube Kanal Linki / Adı", icon: "fa-brands fa-youtube", placeholder: "@SAHDER", color: "#FF0000" },
  { key: "social_whatsapp", label: "WhatsApp Numarası (uluslararası)", icon: "fa-brands fa-whatsapp", placeholder: "905321234567", color: "#25D366" },
  { key: "social_whatsapp_group", label: "WhatsApp Grubu Davet Linki", icon: "fa-brands fa-whatsapp", placeholder: "https://chat.whatsapp.com/...", color: "#128C7E" },
];

const SocialAndStoriesAdmin = () => {
  const { settings, refresh } = useSettings();
  const [state, setState] = useState(settings);
  const [saving, setSaving] = useState(false);

  const [stories, setStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(true);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { setState(settings); }, [settings]);

  const fetchStories = useCallback(async () => {
    setLoadingStories(true);
    try {
      const { data } = await api.get("/stories");
      setStories(data || []);
    } catch (err) {
      toast.error("Story'ler yüklenemedi");
    } finally {
      setLoadingStories(false);
    }
  }, []);

  useEffect(() => { fetchStories(); }, [fetchStories]);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        social_enabled: !!state.social_enabled,
        social_instagram: state.social_instagram || "",
        social_facebook: state.social_facebook || "",
        social_youtube: state.social_youtube || "",
        social_whatsapp: state.social_whatsapp || "",
        social_whatsapp_group: state.social_whatsapp_group || "",
        ig_username: state.ig_username || "",
        ig_business_account_id: state.ig_business_account_id || "",
        ig_access_token: state.ig_access_token || "",
        ig_auto_sync: !!state.ig_auto_sync,
      };
      await apiAdmin.put("/settings", payload);
      await refresh();
      toast.success("Sosyal medya ayarları kaydedildi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const startNew = () => setEditing({ image_url: "", caption: "", link: "", order: stories.length + 1, isNew: true });
  const startEdit = (s) => setEditing({ ...s, isNew: false });

  const saveStory = async () => {
    if (!editing?.image_url) { toast.error("Görsel zorunlu"); return; }
    try {
      const payload = {
        image_url: editing.image_url,
        caption: editing.caption || "",
        link: editing.link || "",
        order: Number(editing.order) || 0,
      };
      if (editing.isNew) {
        await apiAdmin.post("/stories", payload);
        toast.success("Story eklendi");
      } else {
        await apiAdmin.put(`/stories/${editing.id}`, payload);
        toast.success("Story güncellendi");
      }
      setEditing(null);
      fetchStories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const deleteStory = async (s) => {
    if (!window.confirm("Bu story'yi silmek istiyor musunuz?")) return;
    try {
      await apiAdmin.delete(`/stories/${s.id}`);
      toast.success("Silindi");
      fetchStories();
    } catch { toast.error("Silinemedi"); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await apiAdmin.post("/stories/sync-instagram");
      toast.success(`Senkronize edildi: ${data.added} yeni, ${data.updated} güncellenmiş`);
      await refresh();
      fetchStories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Senkronize edilemedi");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="social-stories-admin">
      {/* Social media settings */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-[#8A252C]/10 flex items-center justify-center">
            <i className="fa-solid fa-share-nodes text-lg text-[#8A252C]" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Sosyal Medya</div>
            <h3 className="font-display text-xl font-bold text-[#1F2937]">Dernek Hesapları</h3>
            <p className="text-sm text-[#4B5563] mt-0.5">
              Kullanıcı adı yeterli (tam URL de yapıştırabilirsiniz). Boş bıraktığınız hesaplar üye sayfasında görünmez.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#4B5563]">Aktif</span>
            <Switch checked={!!state.social_enabled} onCheckedChange={(v) => set("social_enabled", v)} data-testid="social-enabled-toggle" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SOCIAL_FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold flex items-center gap-1.5">
                <i className={f.icon} style={{ color: f.color }} />
                {f.label}
              </Label>
              <Input value={state[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                placeholder={f.placeholder} data-testid={`${f.key}-input`}
                className="mt-1 bg-stone-50 border-stone-300 h-11" />
            </div>
          ))}
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="social-save-btn"
            className="bg-[#8A252C] hover:bg-[#6f1d24] text-white gap-2 rounded-xl h-11 px-5">
            <Save className="w-4 h-4" />
            {saving ? "Kaydediliyor..." : "Sosyal Medyayı Kaydet"}
          </Button>
        </div>
      </div>

      {/* Stories management */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center">
              <i className="fa-brands fa-instagram text-lg text-white" aria-hidden="true" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Instagram Otomatik Bağlantı</div>
              <h3 className="font-display text-xl font-bold text-[#1F2937]">Story'yi @{state.ig_username || "hesap"}'tan çek</h3>
              <p className="text-xs text-[#4B5563] mt-0.5 max-w-xl">
                Meta Business Suite &rarr; Instagram Business hesabı + Facebook Page bağlantısı + uzun ömürlü
                <b> Access Token</b> gereklidir. Gir + kaydet, sonra "Şimdi Senkronize Et"e bas veya otomatik
                senkronu aç (6 saatte bir çalışır).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-semibold text-[#4B5563]">Otomatik</span>
            <Switch checked={!!state.ig_auto_sync} onCheckedChange={(v) => set("ig_auto_sync", v)} data-testid="ig-auto-sync-toggle" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Kullanıcı Adı</Label>
            <Input value={state.ig_username || ""} onChange={(e) => set("ig_username", e.target.value)}
              placeholder="sahder25" data-testid="ig-username-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Business Account ID</Label>
            <Input value={state.ig_business_account_id || ""} onChange={(e) => set("ig_business_account_id", e.target.value)}
              placeholder="17841400..." data-testid="ig-business-id-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11 font-mono" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Access Token</Label>
            <Input type="password" value={state.ig_access_token || ""} onChange={(e) => set("ig_access_token", e.target.value)}
              placeholder="EAAG..." data-testid="ig-token-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11 font-mono" />
          </div>
        </div>

        {state.ig_last_sync && (
          <p className="text-[11px] text-[#4B5563] mb-3">
            Son senkronizasyon: {new Date(state.ig_last_sync).toLocaleString("tr-TR")}
          </p>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          <Button onClick={save} disabled={saving} data-testid="ig-save-btn"
            className="bg-[#1F2937] hover:bg-[#111827] text-white gap-2 rounded-xl h-11 px-5">
            <Save className="w-4 h-4" />
            Ayarları Kaydet
          </Button>
          <Button onClick={syncNow} disabled={syncing} data-testid="ig-sync-now-btn"
            className="bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 text-white gap-2 rounded-xl h-11 px-5">
            <Bolt className="w-4 h-4" />
            {syncing ? "Senkronize ediliyor..." : "Şimdi Senkronize Et"}
          </Button>
        </div>

        <div className="flex items-center justify-between gap-3 mb-4 pt-4 border-t border-stone-200">
          <div>
            <h4 className="font-display text-lg font-bold text-[#1F2937]">Mevcut Story'ler ({stories.length})</h4>
            <p className="text-xs text-[#4B5563]">Otomatik çekilenler + manuel eklenenler.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchStories} data-testid="stories-refresh-btn" className="border-stone-300 gap-2 h-10">
              <RefreshCw className="w-4 h-4" /> Yenile
            </Button>
            <Button onClick={startNew} data-testid="story-add-btn" className="bg-[#8A252C] hover:bg-[#6f1d24] text-white gap-2 rounded-xl h-10">
              <Plus className="w-4 h-4" /> Manuel Story
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {loadingStories ? (
            <p className="col-span-full text-sm text-[#4B5563] text-center py-4">Yükleniyor...</p>
          ) : stories.length === 0 ? (
            <p className="col-span-full text-sm text-[#4B5563] text-center py-6">Henüz story yok. Instagram'ı bağlayıp senkronize edin veya "Manuel Story" ekleyin.</p>
          ) : (
            stories.map((s) => (
              <div key={s.id} data-testid={`story-row-${s.id}`}
                className="group relative rounded-xl overflow-hidden border border-stone-200 bg-white">
                <button type="button" onClick={() => startEdit(s)} className="block w-full aspect-square bg-stone-100">
                  <img src={resolveUploadUrl(s.image_url)} alt={s.caption || "Story"} className="w-full h-full object-cover" />
                </button>
                {s.caption && (
                  <div className="px-2 py-1 text-[11px] text-[#1F2937] truncate">{s.caption}</div>
                )}
                <button type="button" onClick={() => deleteStory(s)}
                  data-testid={`story-delete-${s.id}`}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Sil">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Story edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg bg-[#F9F6F0] border-stone-200 max-h-[92vh] overflow-y-auto" data-testid="story-dialog">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-2xl text-[#1F2937]">
              {editing?.isNew ? "Yeni Story" : "Story Düzenle"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold flex items-center gap-1.5">
                <ImageIcon className="w-3 h-3" /> Görsel
              </Label>
              <div className="mt-2">
                <InvitationUploader
                  value={editing?.image_url || ""}
                  onChange={(url) => setEditing((e) => ({ ...e, image_url: url }))}
                  compact
                  title="Story görselini seçin"
                  hint="Dikey (9:16) tercih edilir · en fazla 10 MB"
                  doneLabel="Story görseli yüklendi"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Kısa Açıklama (opsiyonel)</Label>
              <Input value={editing?.caption || ""} onChange={(e) => setEditing((s) => ({ ...s, caption: e.target.value }))}
                placeholder="Örn: Kına gecemiz" data-testid="story-caption-input"
                className="mt-1 bg-white border-stone-300 h-11" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold flex items-center gap-1.5">
                <LinkIcon className="w-3 h-3" /> Bağlantı (opsiyonel)
              </Label>
              <Input value={editing?.link || ""} onChange={(e) => setEditing((s) => ({ ...s, link: e.target.value }))}
                placeholder="https://instagram.com/p/..." data-testid="story-link-input"
                className="mt-1 bg-white border-stone-300 h-11" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Sıra</Label>
              <Input type="number" value={editing?.order ?? 0} onChange={(e) => setEditing((s) => ({ ...s, order: Number(e.target.value) }))}
                data-testid="story-order-input"
                className="mt-1 bg-white border-stone-300 h-11" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} className="border-stone-300 h-11" data-testid="story-cancel-btn">Vazgeç</Button>
            <Button onClick={saveStory} className="bg-[#8A252C] hover:bg-[#6f1d24] text-white h-11 gap-2" data-testid="story-save-btn">
              <Save className="w-4 h-4" /> Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialAndStoriesAdmin;
